import React, { useState } from 'react'

import MoneyField     from 'src/components/MoneyField'
import SandwichField  from 'src/components/SandwichField'

const Amount = ({
  i18n, donation, currencies, errors, show_input_labels, traditional_flat_style,
  multiOrg, multiProject,
}) => {
  const [ focused, setFocused ] = useState(false)
  const error = errors?.amount?.[0]
  const currencyFormat = i18n.currency.format.replace('%c', '').trim()
  const currencyFormatParts = currencyFormat.split('%n')

  // Impact-callout gating mirrors v7's Amount.jsx: no impact line on a
  // fundraiser that splits across orgs or offers a choice of projects.
  // Impact metrics are amount-specific and per-beneficiary, so they
  // don't compose meaningfully in either case. A sole designation is
  // applied without asking the donor, so it keeps its impact line.
  //
  // Both flags come from useBeneficiarySelect, which only the fiat and
  // deferred forms use. The mini and terminal widgets render this same
  // component from their own configs, which carry no partner_items or
  // projects to derive them from — so they pass neither and show the
  // callout unconditionally, as they did before either flag existed.
  const activePreset = !multiOrg && !multiProject
    && donation.presets.find(p => donation.bigAmount.eq(p.amount))

  return (
    <div className="mb-3 row-amount">
      <label
        className={show_input_labels ? 'form-label' : 'visually-hidden'}
        htmlFor="amount"
        aria-hidden="true"
      >
        {i18n.t('amount')}
      </label>

      <label
        className={[
          'form-control form-control-lg overflow-hidden',
          currencies.length > 1 ? 'px-2' : 'ps-3 pe-2',
          focused ? 'focus' : null,
          error ? 'is-invalid' : null,
        ].filter(Boolean).join(' ')}
        style={{ backgroundImage: 'none' }} // remove validation image
        htmlFor="amount"
      >
        <div className="d-flex align-items-center flex-wrap gap-3">
          {
            currencies.length > 1 &&
            <select
              id="currency"
              className={[
                'form-select form-select-sm flex-grow-0 w-auto',
                traditional_flat_style && 'bg-light border-light form-select-light',
              ].filter(Boolean).join(' ')}
              value={donation.currency.id}
              onChange={e => donation.setCurrency(e.target.value)}
              aria-label={i18n.t('currency')}
            >
              {currencies.map(currency =>
                <option key={currency.id} value={currency.id}>
                  {currency.iso_code}
                </option>
              )}
            </select>
          }

          <div style={{ flex: '20 1 auto', width: '1%', minWidth: '12ch' }}>
            <div className="m-n2">
              <SandwichField
                type={MoneyField}
                id="amount"
                className="money-field border-0 w-100 bg-transparent"
                style={{ padding: '0.5rem' }} // avoid !important
                prefix={(prefix =>
                  prefix && <small className="text-body-secondary"> {prefix} </small>
                )(currencyFormatParts[0].replace('%u', donation.currency.symbol || ''))}
                suffix={(suffix =>
                  suffix && <small className="text-body-secondary"> {suffix} </small>
                )([
                  currencyFormatParts[1].replace('%u', donation.currency.symbol || ''),
                  currencies.length > 1 ? null : donation.currency.iso_code,
                ].filter(Boolean).join(' '))}
                exponent={donation.currency.exponent}
                decimal={i18n.currency.separator}
                placeholder={i18n.t('amount')}
                value={donation.state.amount}
                onChange={e => donation.setAmount(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                autoComplete="off"
                required
                aria-label={i18n.t('amount')}
                aria-describedby={error && 'amount-error'}
                aria-invalid={error ? 'true' : null}
                aria-required="true"
              />
            </div>
          </div>

          <div className="row-amounts flex-grow-1">
            <div
              className="d-flex flex-wrap align-items-center column-gap-2"
              role="group"
              aria-labelledby="suggestedAmountsLabel"
            >
              <div className="w-100 border-top" style={{ marginTop: 'calc(-1rem - 1px)' }} />

              <span
                className={show_input_labels ? 'text-body-secondary fs-6' : 'visually-hidden'}
                id="suggestedAmountsLabel"
              >
                <small>{i18n.t('suggested_amounts')}</small>
              </span>

              <div className="flex-fill">
                <div className="amount-options d-flex flex-wrap gap-1">
                  {donation.presets.map((preset, i) => {
                    const id = `amountOption${i}`

                    return (
                      <div key={i} className="flex-grow-1 d-grid">
                        <input
                          type="radio"
                          className="btn-check amount-option-btn"
                          id={id}
                          name="preset_amount"
                          value={preset.amount}
                          checked={donation.bigAmount.eq(preset.amount)}
                          onChange={() => donation.setAmount(preset.amount, i)}
                        />
                        <label
                          className="btn btn-sm btn-light btn-check-special amount-option-label"
                          htmlFor={id}
                        >
                          {i18n.formatCurrency(preset.amount, donation.currency)}
                        </label>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </label>

      {activePreset?.impact && (
        <div className="d-flex align-items-center gap-2 mt-2 bg-light rounded-3 p-3">
          {activePreset.emoji && (
            <span className="fs-4" aria-hidden="true">{activePreset.emoji}</span>
          )}
          <span className="fw-semibold">
            {i18n.formatCurrency(donation.state.amount, donation.currency)}
            {' '}
            {activePreset.impact}
          </span>
        </div>
      )}

      {
        error &&
        <div id="amount-error" className="invalid-feedback">{error}</div>
      }
    </div>
  )
}

export default Amount
